import { AppSelect } from '../../../components/Select'
import styles from './SearchAdvancedFilter.module.css'

type SearchFieldOption = { label: string; value: string }

// The selector sits inside the search field, which already draws the border, the fill and the
// separator — so its own control chrome and indicator separator are removed. Colours stay with
// AppSelect's palette.
const SELECTOR_STYLES = {
  container: (base: any) => ({ ...base, width: '100%' }),
  control: (base: any) => ({
    ...base,
    border: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
}

interface SearchAdvancedFilterProps {
  value: { field: string; query: string }
  onChange: (v: { field: string; query: string }) => void
  options: SearchFieldOption[]
  label?: string
  placeholder?: string
  /** Validation message shown under the field; the page owns the check and the text. */
  error?: string
}

/**
 * "Search + which field" combined filter for FilterManager.
 *
 * Single visual block styled as one AppSelect control: the text input on the
 * left, a vertical separator, and the field selector (AppSelect) on the right.
 * Mounts under the `'searchAdvanced'` key.
 * Value shape: `{ field: string; query: string }`.
 *
 * @example
 * <FilterManager
 *   activeFilters={['searchAdvanced']}
 *   defaultValues={{ searchAdvanced: { field: 'id', query: '' } }}
 *   filterProps={{
 *     searchAdvanced: {
 *       label: 'Поиск',
 *       options: [
 *         { value: 'id',    label: 'По ID' },
 *         { value: 'token', label: 'По токену' },
 *       ],
 *       placeholder: 'Введите значение…',
 *     }
 *   }}
 *   onApplyFilters={(f) => console.log(f.searchAdvanced)}
 * />
 */
export function SearchAdvancedFilter({
  value,
  onChange,
  options = [],
  placeholder = 'Поиск',
  label = 'Поиск',
  error,
}: SearchAdvancedFilterProps) {
  const selected = options.find((o) => o.value === value.field) ?? null

  return (
    <>
      {label && (
        <label className='fs-6 form-label fw-bolder text-dark'>{label}</label>
      )}
      <div className={`${styles.field}${error ? ` ${styles.invalid}` : ''}`}>
        <span className='svg-icon svg-icon-3 svg-icon-gray-500 ms-4'>
          <i className='fa fa-search'></i>
        </span>
        <input
          type='text'
          name='searchAdvanced'
          className={`form-control ps-3 ${styles.input}`}
          aria-invalid={error ? true : undefined}
          placeholder={placeholder}
          value={value.query}
          onChange={(e) => onChange({ ...value, query: e.target.value })}
        />
        <span className={styles.separator} aria-hidden='true' />
        <div className={styles.selector}>
          {/* Invisible copy of every label: the browser sizes the column to the widest one. */}
          <div className={styles.selectorSizer} aria-hidden='true'>
            {options.map((o) => (
              <div key={o.value}>{o.label}</div>
            ))}
          </div>
          <AppSelect<SearchFieldOption>
            aria-label='Поле поиска'
            options={options}
            value={selected}
            isSearchable={false}
            onChange={(opt) => opt && onChange({ ...value, field: opt.value })}
            styles={SELECTOR_STYLES}
          />
        </div>
      </div>
      {error && <div className='text-danger fs-7 mt-1'>{error}</div>}
    </>
  )
}
